import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Masonry from "react-masonry-css";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { categories } from "../../content";
import { getThumbnailPath } from "../../shared/getThumbnailPath";

// Добавляем стили для прозрачного фона лайтбокса
import "./lightbox-custom.css";

const CategoryGallery = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  // Находим категорию по ID
  useEffect(() => {
    setIsLoading(true);
    const foundCategory = categories.find((cat) => cat.id === categoryId);
    setCategory(foundCategory);
    setIsLoading(false);
  }, [categoryId]);

  // Обработчик клика по изображению для открытия лайтбокса
  const handleImageClick = (index) => {
    setImageIndex(index);
    setOpen(true);
  };

  // Создаем структурированные данные для галереи
  const generateStructuredData = (cat) => {
    if (!cat) return null;

    return {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: `Фотозона ${cat.title} | Лесная Усадьба`,
      description: cat.description,
      url: `https://fotodacha.ru/#/gallery/${cat.id}`,
      image: cat.preview,
      associatedMedia: cat.images.map((img, index) => ({
        "@type": "ImageObject",
        contentUrl: img.src,
        name: img.alt,
        description: `Фотозона ${cat.title} - изображение ${
          index + 1
        } в Подмосковье`,
      })),
    };
  };

  // Создаем структурированные данные для хлебных крошек
  const generateBreadcrumbData = (cat) => {
    if (!cat) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Главная",
          item: "https://fotodacha.ru/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Галерея фотолокаций",
          item: "https://fotodacha.ru/#/gallery",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: cat.title,
          item: `https://fotodacha.ru/#/gallery/${cat.id}`,
        },
      ],
    };
  };

  // Создаем структурированные данные для рейтинга
  const generateRatingData = (cat) => {
    if (!cat) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `Фотозона ${cat.title} в Подмосковье`,
      image: cat.preview,
      description: cat.description,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "27",
        bestRating: "5",
        worstRating: "1",
      },
    };
  };

  // Устанавливаем метатеги для SEO
  useEffect(() => {
    if (category) {
      // Устанавливаем заголовок страницы
      document.title = `Фотозона "${category.title}" | Аренда локации для фотосессии в Подмосковье`;

      // Устанавливаем мета-описание
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute(
          "content",
          `Фотозона "${category.title}" в Лесной Усадьбе. ${category.description} Бронируйте фотосессию в Подмосковье.`
        );
      }

      // Устанавливаем canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute(
        "href",
        `https://fotodacha.ru/#/gallery/${category.id}`
      );

      // Добавляем структурированные данные для галереи
      const structuredData = generateStructuredData(category);
      let galleryScriptTag = document.querySelector(
        "#category-structured-data"
      );
      if (!galleryScriptTag) {
        galleryScriptTag = document.createElement("script");
        galleryScriptTag.id = "category-structured-data";
        galleryScriptTag.type = "application/ld+json";
        document.head.appendChild(galleryScriptTag);
      }
      galleryScriptTag.textContent = JSON.stringify(structuredData);

      // Добавляем структурированные данные для хлебных крошек
      const breadcrumbData = generateBreadcrumbData(category);
      let breadcrumbScriptTag = document.querySelector(
        "#breadcrumb-structured-data"
      );
      if (!breadcrumbScriptTag) {
        breadcrumbScriptTag = document.createElement("script");
        breadcrumbScriptTag.id = "breadcrumb-structured-data";
        breadcrumbScriptTag.type = "application/ld+json";
        document.head.appendChild(breadcrumbScriptTag);
      }
      breadcrumbScriptTag.textContent = JSON.stringify(breadcrumbData);

      // Добавляем структурированные данные для рейтинга
      const ratingData = generateRatingData(category);
      let ratingScriptTag = document.querySelector("#rating-structured-data");
      if (!ratingScriptTag) {
        ratingScriptTag = document.createElement("script");
        ratingScriptTag.id = "rating-structured-data";
        ratingScriptTag.type = "application/ld+json";
        document.head.appendChild(ratingScriptTag);
      }
      ratingScriptTag.textContent = JSON.stringify(ratingData);

      // Очистка при размонтировании
      return () => {
        if (galleryScriptTag) {
          document.head.removeChild(galleryScriptTag);
        }
        if (breadcrumbScriptTag) {
          document.head.removeChild(breadcrumbScriptTag);
        }
        if (ratingScriptTag) {
          document.head.removeChild(ratingScriptTag);
        }
      };
    }
  }, [category]);

  if (isLoading) {
    return (
      <div className="section bg-linen">
        <div className="container">
          <div className="flex items-center justify-center h-80">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="section bg-linen">
        <div className="container">
          <h2 className="text-3xl font-bold text-center font-serif header-color md:text-4xl mb-8">
            Категория не найдена
          </h2>
          <p className="text-center text-gray-700 mb-8">
            К сожалению, запрашиваемая категория не существует.
          </p>
          <div className="flex justify-center">
            <Link to="/gallery" className="btn btn-primary">
              Вернуться в галерею
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section bg-linen">
      <div className="container">
        {/* Хлебные крошки для навигации и SEO */}
        <nav aria-label="Хлебные крошки" className="breadcrumbs py-2 mb-4">
          <ol className="flex flex-wrap items-center text-sm text-gray-600">
            <li className="breadcrumb-item">
              <Link to="/" className="hover:text-terracotta transition-colors">
                Главная
              </Link>
              <span className="mx-2">/</span>
            </li>
            <li className="breadcrumb-item">
              <Link
                to="/gallery"
                className="hover:text-terracotta transition-colors"
              >
                Галерея фотолокаций
              </Link>
              <span className="mx-2">/</span>
            </li>
            <li className="breadcrumb-item" aria-current="page">
              <span className="text-terracotta font-medium">
                {category.title}
              </span>
            </li>
          </ol>
        </nav>

        <h2 className="text-3xl font-bold text-center font-serif header-color md:text-4xl mb-4">
          Фотозона "{category.title}"
        </h2>

        <p className="text-center text-gray-700 max-w-3xl mx-auto mb-12">
          {category.description}
        </p>

        {/* Рейтинг фотозоны */}
        {/* <div className="flex justify-center items-center mb-8">
          <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
            <div className="flex text-yellow-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <span className="ml-2 text-gray-700">4.9 из 5 (27 отзывов)</span>
          </div>
        </div> */}

        <Masonry
          breakpointCols={{
            default: 4,
            1100: 3,
            700: 2,
            500: 1,
          }}
          className="masonry-grid"
          columnClassName="masonry-grid_column"
        >
          {category.images.map((image, index) => (
            <div
              key={`${image.src}-${index}`}
              className="masonry-item cursor-pointer mb-4"
              onClick={() => handleImageClick(index)}
            >
              <div className="relative overflow-hidden rounded-lg shadow-md">
                <img
                  src={getThumbnailPath(image.src, "md")}
                  alt={`${image.alt} в Подмосковье`}
                  className="w-full object-cover transition-transform hover:scale-105 duration-500"
                  loading="lazy"
                  data-original={image.src}
                  width="300"
                  height="300"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white bg-black/50 rounded-full p-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </Masonry>

        <div className="mt-12 flex justify-center">
          <Link
            to="/gallery"
            className="btn btn-outline mr-4"
            aria-label="Вернуться к списку всех фотозон"
          >
            Вернуться к галерее
          </Link>
          {/* <Link to="/#booking" className="btn btn-primary">
            Забронировать фотосессию
          </Link> */}
        </div>

        {/* Лайтбокс для просмотра изображений */}
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          index={imageIndex}
          slides={category.images.map((img) => ({
            src: img.src,
            alt: img.alt,
          }))}
          plugins={[Zoom, Thumbnails]}
          className="transparent-lightbox" // Добавляем класс для прозрачного фона
          zoom={{
            maxZoomPixelRatio: 3,
            scrollToZoom: true,
          }}
          thumbnails={{
            position: "bottom",
            width: 120,
            height: 80,
            gap: 12,
            padding: 8,
            border: "2px solid rgba(255, 255, 255, 0.3)",
            borderRadius: 4,
          }}
          styles={{
            container: { backgroundColor: "rgba(0, 0, 0, 0.8)" }, // Прозрачный фон контейнера
            root: { backdropFilter: "blur(10px)" }, // Размытие фона
          }}
          render={{
            iconNext: () => (
              <button aria-label="Следующее изображение">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            ),
            iconPrev: () => (
              <button aria-label="Предыдущее изображение">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            ),
            iconClose: () => (
              <button aria-label="Закрыть галерею">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default CategoryGallery;
